// Angular imports
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';

// Modal system imports
import { BaseModalComponent } from '../../modal/base-modal';
import { ModalService } from '../../modal/modal.service';

// Import line items
import { CATEGORIZED_LINE_ITEMS } from '../../line-items/categorized-items';
import { CalculationLineApi, CalculationPercentagesApi,  } from 'src/app/types';
import { ApplicationEventService } from 'src/app/services';
import { InputFieldComponent } from 'src/app/components/input-field/input-field.component';

// Declare types
export type AddRowEvent = {
    code: string;
    unit: string;
    order?: number;
    description: string;
    defaultPercentages?: CalculationPercentagesApi;
    norms?: {
        hours?: number;
        material?: number;
        equipment?: number;
        subcontracting?: number;
    }
}

declare type LibraryItem = {
    name: string;
    items: Partial<CalculationLineApi>[];
    selected: boolean;
}

declare type ItemCategory = {
    name: string;
    open: boolean;
    selected: boolean;
    items: LibraryItem[];
}

export type AddLibraryItemModalArgs = {
    addRows: (lines: CalculationLineApi[]) => void;
}

@Component({
    selector: 'add-library-item-modal',
    templateUrl: './add-library-item-modal.component.html',
    styleUrls: ['./add-library-item-modal.component.scss'],
    standalone: false
})
export class AddLibraryItemModalComponent extends BaseModalComponent implements AfterViewInit {
    // Modal properties
    @ViewChild('descriptionField') public _descriptionField: InputFieldComponent;

    // Modal service
    public title = 'Bibliotheek item toevoegen';

    public canAdd: boolean = false;

    public description: string = '';

    public items: ItemCategory[];
    public filteredItems: ItemCategory[] = [];
    public openCategories: string[] = [];

    private addRowsCallback: (rows: Partial<CalculationLineApi>[]) => void;

    constructor(private modalService: ModalService, private eventService: ApplicationEventService) {
        super();
    }

    public init(args: AddLibraryItemModalArgs) {
        // Set args
        this.addRowsCallback = args.addRows;

        // Load items
        this.items = Object.keys(CATEGORIZED_LINE_ITEMS).map<ItemCategory>(name => {
            return {
                name: name,
                open: false,
                selected: false,
                items: Object.keys(CATEGORIZED_LINE_ITEMS[name]).map<LibraryItem>(itemName => {
                    const item = CATEGORIZED_LINE_ITEMS[name][itemName];

                    return {
                        name: itemName,
                        items: item.items,
                        open: false,
                        selected: false
                    }
                })
            }
        });

        this.items = this.items.filter(cat => {
            return cat.items.length > 0;
        });

        this.items.sort((a, b) => a.name.localeCompare(b.name));
        this.filterStabu('', '');
    }

    public addItems() {
        if (!this.canAdd) return;

        // Filter selected sub items
        const selectedItems = this.items.map(i => i.items).flat().filter(f => f.selected);

        const itemsToAdd = selectedItems.map(s => s.items).flat()
            // Before, the defaultPercentages property of the Line class was used to differentiate between calculation and text lines
            // The front end classes, like Line, are no longer used. All backend classes, like CalculationLineApi, are no used directly in the frontend
            // This means that isTextLine is needed
            .map(m => ({
                ...m,
                isTextLine: !m.unit // Library items have no unit set for text lines
            }));

        // Trigger callback
        this.addRowsCallback(itemsToAdd);

        // Close the modal
        this.modalService.close();
    }

    /* --- Event handlers --- */
    public ngAfterViewInit(): void {
        // Set focus to prevend typing in the underlying calculation
        this._descriptionField?.focus();
    }

    public _onCategorySelect(cat: ItemCategory) {
        this.selectCategory(cat, !cat.selected);
        this.updateCanAdd();
    }

    public _onLibraryItemSelect(cat: ItemCategory, item: LibraryItem) {
        this.selectLibraryItem(cat, item, !item.selected);
        this.updateCanAdd();
    }

    public _onDescriptionChange(value: string) {
        this.description = value.trim();

        this.closeAll();

        if (this.description.length != 0)
            this.filterStabu(null, this.description);
        else {
            this.closeAll();
            this.filterStabu('', '');
        }
    }

    public _onClickCategory(cat: ItemCategory) {
        cat.open = true;

        this.filterStabu(cat.name, null);
    }

    public _onClickOpenCategory(cat: ItemCategory, event: Event) {
        cat.open = !cat.open;

        event.stopPropagation();
    }

    /* --- Helper functions --- */

    private selectCategory(cat: ItemCategory, selected: boolean) {
        cat.selected = selected;
        cat.items.forEach(item => this.selectLibraryItem(cat, item, selected));
    }

    private selectLibraryItem(cat: ItemCategory, item: LibraryItem, selected: boolean) {
        item.selected = selected;

        if (!cat.selected && cat.items.length == cat.items.filter(item => item.selected).length)
            cat.selected = true;
        else if (cat.selected && cat.items.length > cat.items.filter(item => item.selected).length)
            cat.selected = false;
    }

    public onKeydownCode(event: KeyboardEvent) {
        if (event.key != 'Enter') return;

        event.preventDefault();
        event.stopPropagation();

        if (event.key == 'Enter') {
            if (this.canAdd)
                this.addItems();
        }
    }

    public filterStabu(categoryName: string, itemName: string) {
        if (categoryName != null) {
            categoryName = categoryName.toLocaleLowerCase();

            // Get all the categories that match the name
            // const categories = Object.keys(CATEGORIZED_LINE_ITEMS).filter(name => name.toLocaleLowerCase().includes(categoryName));

            // Filter items to category name
            this.filteredItems = this.items.filter(cat => cat.name.toLocaleLowerCase().includes(categoryName));
        } else if (itemName != null) {
            itemName = itemName.toLocaleLowerCase();

            this.filteredItems = this.items.filter(cat => {
                const filteredItems = cat.items.filter(item => item.name.toLowerCase().includes(itemName));

                return filteredItems.length > 0 || cat.name.toLocaleLowerCase().includes(itemName);
            }).map(cat => {
                // Copy the category
                const catCopy: ItemCategory = JSON.parse(JSON.stringify(cat));
                catCopy.items = [...cat.items];

                // Map subs
                const filteredItems = catCopy.items.filter(item => item.name.toLocaleLowerCase().includes(itemName));
                catCopy.items = filteredItems.length > 0 ? filteredItems : catCopy.items;

                catCopy.open = true;

                if (!catCopy.selected && catCopy.items.length == catCopy.items.filter(item => item.selected).length)
                    catCopy.selected = true;
                else if (catCopy.selected && catCopy.items.length > catCopy.items.filter(item => item.selected).length)
                    catCopy.selected = false;

                return catCopy;
            });
        }

        this.filteredItems.sort((a, b) => a.name.localeCompare(b.name));

        this.updateCanAdd();
    }

    private updateCanAdd() {
        this.canAdd = this.items.filter(cat => {
            const items = cat.items.filter(item => item.selected);

            return items.length > 0;
        }).length > 0;
    }

    private closeAll() {
        this.items.forEach(cat => {
            cat.open = false;
        });
        this.filteredItems.forEach(cat => {
            cat.open = false;
        });
    }

    private deselectAll() {
        this.items.forEach(cat => {
            cat.selected = false;

            cat.items.forEach(item => {
                item.selected = false;
            });
        });

        this.filteredItems.forEach(cat => {
            cat.selected = false;

            cat.items.forEach(item => {
                item.selected = false;
            });
        });
    }
}
